// @flow

import * as React from 'react';

import { selectCartIsActive, ProductIcon, type ProductType } from '@gandi/cart';
import { Base } from '@gandi/ui';

import { useAppSelector } from 'src/hooks/useAppSelector';

import ConfirmationStep from './ConfirmationStep';
import Label from './Label';
import RemoveButton from './RemoveButton';

import styles from './TitleRow.scss';

export type Props = {
  /** main line shown in bold */
  label: React.Node,
  /** add Corporate  badge */
  isCorporate?: boolean,
  /** do not allow delete of this product */
  deleteDisabled?: boolean,
  /** action to remove the product */
  handleRemoveAction?: (SyntheticEvent<HTMLButtonElement>) => mixed,
  /** add a warning to the delete confirmation */
  warningContent?: React.Node,
  /** set the line as busy */
  working?: boolean,
  /** icon depending on the product type */
  productType?: ProductType,
};

export default function TitleRow({
  deleteDisabled,
  label,
  isCorporate,
  handleRemoveAction,
  working = false,
  warningContent,
  productType,
}: Props): React.MixedElement {
  // TODO: try to use product.isWorking instead
  const active = useAppSelector(selectCartIsActive);

  const [confirmationStep, setConfirmationStep] = React.useState(false);

  return (
    <TitleRowContainer>
      <Base
        flex
        flexGrow1
        flexWrap
        flexJustifyBetween
        gap="xsmall"
        flexAlignItemsCenter
      >
        <Label isCorporate={isCorporate}>
          <ProductIcon type={productType} className={styles.icon} />
          {label}
        </Label>

        {confirmationStep && handleRemoveAction && (
          <ConfirmationStep
            fireConfirmationStep={setConfirmationStep}
            handleRemoveAction={handleRemoveAction}
            warningContent={warningContent}
          />
        )}
      </Base>
      {handleRemoveAction &&
        !deleteDisabled &&
        active &&
        !working &&
        !confirmationStep && (
          <RemoveButton fireConfirmationStep={setConfirmationStep} />
        )}
    </TitleRowContainer>
  );
}

TitleRow.Error = function TitleRowError({
  label,
  productType,
}: {
  label: Props['label'],
  productType: Props['productType'],
}): React.MixedElement {
  return (
    <TitleRowContainer>
      <Base
        flex
        flexGrow1
        flexWrap
        flexJustifyBetween
        gap="xsmall"
        flexAlignItemsCenter
      >
        <Label>
          <ProductIcon type={productType} className={styles.icon} />
          {label}
        </Label>
      </Base>
    </TitleRowContainer>
  );
};

function TitleRowContainer({
  children,
}: {
  children: React.ChildrenArray<React.Node>,
}): React.MixedElement {
  return (
    <Base className={styles.root}>
      <Base
        flex
        flexAlignItemsCenter
        flexJustifyBetween
        size="big"
        className={styles.main}
      >
        {children}
      </Base>
    </Base>
  );
}
